/**
 * List pages для Block 2.2 (2026-05-26).
 * Editor + modal — TECHDEBT (окрема міні-порція).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  sickLeave,
  sickLeaveCalculation,
  type SickLeave,
  type SickLeaveCalculation,
} from '@/api/sick_leave';
import { refs } from '@/api/refs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';


const fmt2 = (v: string | number | null) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


const SICK_LEAVE_TYPE_LABELS: Record<string, string> = {
  GENERAL_ILLNESS: 'Загальне захворювання',
  CHILD_CARE: 'Догляд за дитиною',
  MATERNITY: 'Вагітність і пологи',
  WORK_ACCIDENT: 'Виробничий нещасний випадок',
};


export function SickLeavesPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['sick-leaves'],
    queryFn: () => sickLeave.list({ limit: 200 }),
  });
  const empQ = useQuery({ queryKey: ['employees-all'], queryFn: () => refs.employees() });
  const empName = useMemo(() => {
    const m = new Map<string, string>();
    empQ.data?.forEach((e) => m.set(e.id, e.name));
    return m;
  }, [empQ.data]);
  const cols = useMemo<ColumnDef<SickLeave>[]>(
    () => [
      { key: 'document_number', header: '№', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 130,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '',
      },
      { key: 'employee_id', header: 'Працівник', width: 220, sortValue: (r) => empName.get(r.employee_id) ?? r.employee_id, cell: (r) => empName.get(r.employee_id) ?? '—' },
      {
        key: 'sick_leave_type', header: 'Тип', width: 200,
        sortValue: (r) => SICK_LEAVE_TYPE_LABELS[r.sick_leave_type] ?? r.sick_leave_type,
        cell: (r) => SICK_LEAVE_TYPE_LABELS[r.sick_leave_type] ?? r.sick_leave_type,
      },
      { key: 'start_date', header: 'З', width: 110, sortValue: (r) => r.start_date ?? '', cell: (r) => r.start_date },
      { key: 'end_date', header: 'По', width: 110, sortValue: (r) => r.end_date ?? '', cell: (r) => r.end_date },
      {
        key: 'days_count', header: 'Днів', width: 80, align: 'right',
        sortValue: (r) => r.days_count, cell: (r) => r.days_count,
      },
      { key: 'certificate_number', header: 'Довідка', width: 120, sortValue: (r) => r.certificate_number ?? '', cell: (r) => r.certificate_number },
      {
        key: 'insurance_stage_years', header: 'Стаж', width: 90, align: 'right',
        sortValue: (r) => r.insurance_stage_years ?? '',
        cell: (r) => r.insurance_stage_years ? `${r.insurance_stage_years} р.` : '',
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => r.posted ? 1 : 0, cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [empName],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Лікарняні</h2>
        <button className="primary" onClick={() => navigate('/hr/sick-leaves/new')}>
          + Новий лікарняний
        </button>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<SickLeave>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/hr/sick-leaves/${r.id}`)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


export function SickLeaveCalculationsPage() {
  const list = useQuery({
    queryKey: ['sick-leave-calcs'],
    queryFn: () => sickLeaveCalculation.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<SickLeaveCalculation>[]>(
    () => [
      { key: 'document_number', header: '№', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 130,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '',
      },
      { key: 'employee_id', header: 'Працівник', width: 220, sortValue: (r) => r.employee_id ?? '', cell: (r) => r.employee_id },
      {
        key: 'sick_leave_type', header: 'Тип', width: 200,
        sortValue: (r) => SICK_LEAVE_TYPE_LABELS[r.sick_leave_type] ?? r.sick_leave_type,
        cell: (r) => SICK_LEAVE_TYPE_LABELS[r.sick_leave_type] ?? r.sick_leave_type,
      },
      {
        key: 'days_count', header: 'Днів', width: 80, align: 'right',
        sortValue: (r) => r.days_count, cell: (r) => r.days_count,
      },
      {
        key: 'employer_amount', header: 'Роботодавець', width: 130, align: 'right',
        sortValue: (r) => Number(r.employer_amount), cell: (r) => fmt2(r.employer_amount),
      },
      {
        key: 'pfu_amount', header: 'ПФУ/Фонд', width: 130, align: 'right',
        sortValue: (r) => Number(r.pfu_amount), cell: (r) => fmt2(r.pfu_amount),
      },
      {
        key: 'total_gross_amount', header: 'Нараховано', width: 140, align: 'right',
        sortValue: (r) => Number(r.total_gross_amount), cell: (r) => fmt2(r.total_gross_amount),
      },
      {
        key: 'net_amount', header: 'До виплати', width: 140, align: 'right',
        sortValue: (r) => Number(r.net_amount), cell: (r) => fmt2(r.net_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => r.posted ? 1 : 0, cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Розрахунки лікарняних</h2>
      </div>
      <DataTable<SickLeaveCalculation>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
